import type { JSX } from 'react'
import type { IRowContainerProps, ISnapAlign } from '../RowContainer.h'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ButtonCorners } from 'atoms/Button'
import { CARDS_ASPECT_RATIO } from 'blocks/Cards/Card.h'
import { useClientResolution } from 'utils/hooks'
import { FIXED_INDENTS, LAYOUT, ROW_GAP } from 'vars/index'
import { NextControl, PrevControl, RowTitleDescription } from '../components'
import { useSimpleRowScrollHandlers } from '../hooks'
import {
  CarouselWrapperControlSC,
  CarouselWrapperSC,
  RowContainerSC,
} from '../NewRowContainer.styled'
import { getRowScrollControlsVisibility } from '../row-scroll-controls'
import {
  RowContainerVerticalGaps,
  RowControlsType,
  RowDescriptionsMap,
  ScrollBehavior,
  SnapAlign,
} from '../RowContainer.h'
import { SimpleCarouselScrollableComponentSC } from './SimpleCarouselScrollable.styled'

export const RowSimpleContainer = ({
  children,

  itemCount,
  isMoreThanOnePage,
  onSeeAllClick,
  buttonTitle,
  selectedItem,
  disableScrollOnSelectItem,
  cellsAlignment,
  gap = ROW_GAP.SIZE_10,

  horizontalPaddings = LAYOUT.HorizontalPaddings,
  verticalPaddingTop = FIXED_INDENTS.INDENT_0,
  verticalPaddingBottom = FIXED_INDENTS.INDENT_0,
  verticalMarginTop = FIXED_INDENTS.INDENT_0,
  verticalMarginBottom = FIXED_INDENTS.INDENT_0,
  title,
  withSpecialCell,
  descriptionType,
  description,
  backgroundStyle,
  backgroundGradient,
  rowPosterImage,

  cardAspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_TRIA_DEFAULT,
  cardAspectRatioVar,
  controlsType,
  titleColor,
  containerVerticalGap,
  withCardDescription = false,
  matchPosterHeight = false,
  cornersButtonLeft = ButtonCorners.CORNERS_RIGHT_25,
  cornersButtonRight = ButtonCorners.CORNERS_LEFT_25,
  visibleCards = 0,
  innerRef,
  dataTestIdRow,
}: IRowContainerProps): JSX.Element => {
  const rowRef = useRef<HTMLDivElement>(null)

  const { width: clientWidth } = useClientResolution()

  const [childrenCount, setChildrenCount] = useState<number>()
  const [snapAlign, setSnapAlign] = useState<ISnapAlign>(SnapAlign.unset)

  //TODO: Fixes bug FP-2112. You need to look for a solution through css properties.
  useEffect(() => {
    setSnapAlign(SnapAlign.start)
  }, [])

  const isRenderSpecialCell = useMemo(
    () => Boolean(clientWidth >= 600 && withSpecialCell && descriptionType),
    [withSpecialCell, clientWidth, descriptionType, withSpecialCell]
  )

  const controlsInHeader = useMemo(
    () => controlsType === RowControlsType.inHeader,
    [controlsType]
  )

  const Description = RowDescriptionsMap[descriptionType!]

  const extraCount = useMemo(() => {
    let extraCountWithSpecial = 1
    if (visibleCards > 3 && clientWidth >= 768) extraCountWithSpecial = 2
    if (visibleCards > 5) extraCountWithSpecial = 3

    if (isRenderSpecialCell) return extraCountWithSpecial
    return 0
  }, [
    visibleCards,
    isRenderSpecialCell,
    clientWidth,
    withSpecialCell,
    descriptionType,
  ])

  useEffect(() => {
    if (!childrenCount)
      setChildrenCount(() => itemCount! + (isRenderSpecialCell ? 1 : 0))
  }, [isRenderSpecialCell, itemCount, childrenCount])

  const [isNextControlVisible, setIsNextControlVisible] = useState<boolean>()
  const [isPrevControlVisible, setIsPrevControlVisible] = useState<boolean>()

  const updateControlsVisibility = () => {
    if (!rowRef.current) {
      return
    }

    const { isNextVisible, isPrevVisible } = getRowScrollControlsVisibility(
      rowRef.current
    )

    setIsNextControlVisible(isNextVisible)
    setIsPrevControlVisible(isPrevVisible)
  }

  const [rowScrollPosition, setRowScrollPosition] = useState<number>(0)
  const { next, prev, handleScroll, focusOnItem } = useSimpleRowScrollHandlers({
    ref: rowRef,
    visibleCards: visibleCards!,
    setRowScrollPosition,
  })

  useEffect(() => {
    updateControlsVisibility()
  }, [rowScrollPosition, updateControlsVisibility])

  // Chrome on Linux: 100cqw can resolve a frame after mount (fallback is vw,
  // which includes the classic scrollbar gutter). Remeasure when the row or
  // its cards change size so next/prev stay hidden when the cards already fit
  // (FP-3448). Observe children because card width changes do not resize the
  // row's border box. MutationObserver keeps child observations in sync
  // without depending on `children` (DynamicModule recreates that element
  // every render).
  useLayoutEffect(() => {
    const container = rowRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      updateControlsVisibility()
    })

    const observeTree = () => {
      resizeObserver.observe(container)
      for (const child of container.children) {
        resizeObserver.observe(child)
      }
    }

    observeTree()

    const mutationObserver = new MutationObserver(() => {
      resizeObserver.disconnect()
      observeTree()
      updateControlsVisibility()
    })
    mutationObserver.observe(container, { childList: true })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
    // visibleCards gates the scrollable mount (`return visibleCards ? …`).
  }, [visibleCards])

  // note that current realisation is suitable only for cases where 1 row at page (not categories pages)
  useLayoutEffect(() => {
    if (selectedItem !== undefined) {
      focusOnItem({
        // need to scroll catchups row to selelcted item on init page
        behavior: ScrollBehavior.auto,
        scrollItemIndex: selectedItem,
      })
    }
  }, [])

  useLayoutEffect(() => {
    if (selectedItem !== undefined && !disableScrollOnSelectItem) {
      focusOnItem({
        behavior: ScrollBehavior.auto,
        scrollItemIndex: selectedItem,
      })
    }
  }, [selectedItem, disableScrollOnSelectItem])

  const isRenderDescriptionInHeader: boolean = useMemo(() => {
    return Boolean(
      (withSpecialCell && !isRenderSpecialCell) ||
      (!withSpecialCell && descriptionType)
    )
  }, [descriptionType, isRenderSpecialCell, withSpecialCell])

  const isRenderHeader: boolean = useMemo(() => {
    return controlsInHeader || isRenderDescriptionInHeader || !descriptionType
  }, [descriptionType, controlsInHeader, isRenderDescriptionInHeader])

  const isRenderSpecialTitle =
    Boolean(isRenderSpecialCell) &&
    descriptionType &&
    Boolean(title || description)

  return visibleCards ? (
    <RowContainerSC
      horizontalPaddings={horizontalPaddings}
      verticalPaddingTop={verticalPaddingTop}
      verticalMarginTop={verticalMarginTop}
      verticalMarginBottom={verticalMarginBottom}
      verticalPaddingBottom={verticalPaddingBottom}
      containerVerticalGap={
        isRenderHeader
          ? containerVerticalGap
          : RowContainerVerticalGaps.RowWithoutHeader
      }
      backgroundStyle={backgroundStyle}
      rowPosterImage={rowPosterImage}
      backgroundGradient={backgroundGradient}
      isRenderSpecialCell={Boolean(descriptionType)}
      matchPosterHeight={matchPosterHeight}
      withCardDescription={withCardDescription}
      ref={innerRef}
    >
      {isRenderHeader && (
        <RowTitleDescription
          title={title}
          descriptionType={descriptionType}
          prev={prev}
          next={next}
          nextButtonVisible={isNextControlVisible!}
          prevButtonVisible={isPrevControlVisible!}
          controlsType={controlsType}
          withSpecialCell={isRenderSpecialCell}
          description={description}
          titleColor={titleColor!}
          buttonTitle={buttonTitle}
          onSeeAllClick={onSeeAllClick}
          isMoreThanOnePage={isMoreThanOnePage}
        >
          {isRenderDescriptionInHeader && (
            <Description
              isButtonShown={isMoreThanOnePage}
              description={description}
              onSeeAllClick={onSeeAllClick}
              title={title}
              buttonTitle={buttonTitle}
              paddings={[FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings]}
            />
          )}
        </RowTitleDescription>
      )}

      <CarouselWrapperSC
        gap={gap}
        visibleCards={visibleCards}
        cardAspectRatio={cardAspectRatio}
        cardAspectRatioVar={cardAspectRatioVar}
        matchPosterHeight={matchPosterHeight}
      >
        {isPrevControlVisible && !controlsInHeader && (
          <CarouselWrapperControlSC>
            <PrevControl
              controlsType={controlsType!}
              onClick={prev}
              corners={cornersButtonLeft}
            />
          </CarouselWrapperControlSC>
        )}
        <SimpleCarouselScrollableComponentSC
          gap={gap}
          ref={rowRef}
          withSpecialCell={isRenderSpecialCell}
          visibleCards={visibleCards}
          extraCount={extraCount}
          cellsAlignment={cellsAlignment}
          cardAspectRatio={cardAspectRatio}
          cardAspectRatioVar={cardAspectRatioVar}
          onScroll={handleScroll}
          withCardDescription={withCardDescription}
          snapAlign={snapAlign}
          data-testid={dataTestIdRow}
        >
          {isRenderSpecialTitle && (
            <Description
              description={description}
              onSeeAllClick={onSeeAllClick}
              title={title}
              buttonTitle={buttonTitle}
              isButtonShown={isMoreThanOnePage}
              paddings={[FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings]}
            />
          )}
          {children}
        </SimpleCarouselScrollableComponentSC>
        {isNextControlVisible && !controlsInHeader && (
          <CarouselWrapperControlSC>
            <NextControl
              controlsType={controlsType!}
              onClick={next}
              corners={cornersButtonRight}
            />
          </CarouselWrapperControlSC>
        )}
      </CarouselWrapperSC>
    </RowContainerSC>
  ) : (
    <></>
  )
}
